import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ContentPolicyModal
 *
 * Shown when a file upload is rejected by Rekognition.
 * Cannot be dismissed until the user opens the Terms of Service link
 * and clicks Accept.
 *
 * Triggered globally via:
 *   window.dispatchEvent(new CustomEvent('content-policy-violation'))
 *
 * Mount once in App.jsx:
 *   <ContentPolicyModal />
 */
export default function ContentPolicyModal() {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [termsOpened, setTermsOpened] = useState(false);

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setTermsOpened(false);
    };
    window.addEventListener('content-policy-violation', handler);
    return () => window.removeEventListener('content-policy-violation', handler);
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[9999]">
      <div className={`${theme.card} rounded-2xl p-6 w-full max-w-md shadow-2xl`}>

        {/* Icon + Title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-red-600">Content Policy Violation</h2>
        </div>

        {/* Message */}
        <p className={`text-sm ${theme.text} mb-3`}>
          This file violates our content policy and cannot be uploaded.
        </p>
        <p className={`text-sm ${theme.text} mb-3`}>
          The content has been removed and will not be visible to others. Your account remains active.
        </p>
        <p className={`text-sm ${theme.text} mb-5`}>
          Repeated violations of our content policy may result in your{' '}
          <span className="font-semibold text-red-600">account being suspended</span>.
        </p>

        {/* Terms link */}
        <p className={`text-sm ${theme.textSecondary} mb-5`}>
          Please review our{' '}
          <a
            href="/terms#content-upload-policy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setTermsOpened(true)}
            className="text-blue-500 underline hover:text-blue-600 font-medium"
          >
            Image & Video Upload Policy
          </a>
          {' '}in our Terms of Service to understand what content is prohibited and the consequences of violations.
        </p>

        {!termsOpened && (
          <p className="text-xs text-amber-600 mb-4">
            You must open the Image & Video Upload Policy before accepting.
          </p>
        )}

        {/* Accept button — only enabled after terms link is opened */}
        <button
          onClick={() => setOpen(false)}
          disabled={!termsOpened}
          className="w-full py-2 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          I Understand and Accept
        </button>
      </div>
    </div>
  );
}
